import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GiHamburgerMenu } from "react-icons/gi";
import { IoIosArrowDown } from "react-icons/io";
import { FiCalendar, FiLogOut, FiUser } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../Assets/Images/FooterImages/Logo1.webp";
import { useAuth } from "../../context/AuthContext";
import { X } from "lucide-react";

const BottomNavbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [openUserMenu, setOpenUserMenu] = useState(false);

  const navigate = useNavigate();
  const menuRef = useRef(null);
  const userDropdownRef = useRef(null);
  const location = useLocation();

  const { user, logout } = useAuth();

  const menuItems = [
    { title: "Home", path: "/" },
    { title: "Services", path: "/services" },
    {
      title: "Packages",
      path: "/packages"
    },
    { title: "Catalog", path: "/catalog" },
    { title: "Contact Us", path: "/contact" },
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current?.contains(e.target) ||
        userDropdownRef.current?.contains(e.target)
      )
        return;

      setMenuOpen(false);
      setActiveDropdown(null);
      setOpenUserMenu(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
    setActiveDropdown(null);
    setOpenUserMenu(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLogout = async () => {
    await logout();
    setOpenUserMenu(false);
  };

  return (
    <div className="w-full px-4 sm:px-8 md:px-10 lg:px-12 mx-auto py-2 z-50 bg-white shadow-md">
      {/* Desktop Navbar */}
      <div className="hidden lg:flex items-center justify-between">
        {/* Left Logo */}
        <img
          src={logo}
          alt="logo"
          className="h-14 cursor-pointer"
          onClick={() => handleNavigate("/")}
        />

        {/* Center Nav Links */}
        <div className="flex gap-8 items-center text-base">
          {menuItems.map((item, index) => {
            const isActive =
              item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);
            return (
              <div key={index} className="relative">
                {item.options ? (
                  <div
                    className={`flex items-center gap-1 cursor-pointer hover:text-accentTextColor ${isActive ? "text-accentTextColor font-semibold" : ""
                      }`}
                    onClick={() =>
                      setActiveDropdown(activeDropdown === index ? null : index)
                    }
                  >
                    {item.title} <IoIosArrowDown size={16} />
                  </div>
                ) : (
                  <button
                    className={`relative transition hover:text-accentTextColor ${isActive ? "text-accentTextColor font-semibold" : ""
                      }`}
                    onClick={() => handleNavigate(item.path)}
                  >
                    {item.title}

                    {/* Optional underline */}
                    {isActive && (
                      <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-accentTextColor rounded"></span>
                    )}
                  </button>
                )}

                {item.options && activeDropdown === index && (
                  <div className="absolute bg-white shadow-lg mt-2 rounded-md w-40 z-50">
                    {item.options.map((opt, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleNavigate(opt.path)}
                        className="px-4 py-2 hover:bg-primaryFadedColor cursor-pointer"
                      >
                        {opt.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Right: Auth Section */}
        <div className="relative">
          {user ? (
            <div ref={userDropdownRef}>
              {
                /* User Avatar Button */
                user.profilePicture ? (
                  <img
                    src={user.profilePicture}
                    alt="User Avatar"
                    onClick={() => setOpenUserMenu((p) => !p)}
                    className="w-9 h-9 rounded-full object-cover cursor-pointer"
                  />
                ) : (
                  /* Fallback to Initials Button */
                  <button
                    onClick={() => setOpenUserMenu((p) => !p)}
                    className="w-9 h-9 rounded-full bg-accentTextColor text-white font-semibold flex items-center justify-center"
                  >
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </button>
                )}

              {openUserMenu && (
                <div className="absolute text-sm right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border py-2 z-50">
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => handleNavigate("/user-profile")}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100"
                  >
                    <FiUser /> My Profile
                  </button>

                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => handleNavigate("/my-bookings")}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100"
                  >
                    <FiCalendar className="" />
                    Bookings
                  </button>

                  <div className="border-t my-1" />

                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50"
                  >
                    <FiLogOut /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => handleNavigate("/login")}
              className="px-5 py-2 rounded-md bg-primaryColor text-white hover:opacity-90 active:scale-[0.98] transition-all duration-200"
            >
              Login
            </button>
          )}
        </div>
      </div>

      {/* Mobile Navbar (UNCHANGED) */}
      <div className="lg:hidden flex justify-between items-center">
        <img
          src={logo}
          alt="logo"
          className="h-12 cursor-pointer"
          onClick={() => handleNavigate("/")}
        />
        <div className="flex gap-4 items-center">
          <div className="relative">
            {user ? (
              <div ref={userDropdownRef}>
                {
                  /* User Avatar Button */
                  user.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt="User Avatar"
                      onClick={() => setOpenUserMenu((p) => !p)}
                      className="w-9 h-9 rounded-full object-cover cursor-pointer"
                    />
                  ) : (
                    /* Fallback to Initials Button */
                    <button
                      onClick={() => setOpenUserMenu((p) => !p)}
                      className="w-7 h-7 lg:w-9 lg:h-9 rounded-full bg-accentTextColor text-white font-semibold flex items-center justify-center"
                    >
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </button>
                  )}
                {openUserMenu && (
                  <div className="absolute text-sm  -right-3 mt-2 w-44 bg-white rounded-xl shadow-lg border py-2 z-50">
                    <button
                      onClick={() => handleNavigate("/user-profile")}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100"
                    >
                      <FiUser /> My Profile
                    </button>

                    <button
                      onClick={() => handleNavigate("/my-bookings")}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100"
                    >
                      <FiCalendar className="text-base" />
                      Bookings
                    </button>

                    <div className="border-t my-1" />

                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={handleLogout}
                      className="w-full flex   items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50"
                    >
                      <FiLogOut /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => handleNavigate("/login")}
                className="px-5 py-2 hidden lg:block rounded-md bg-primaryColor text-white hover:opacity-90 active:scale-[0.98] transition-all duration-200"
              >
                Login
              </button>
            )}
          </div>

          <GiHamburgerMenu
            size={28}
            className="text-gray-500 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          />
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            ref={menuRef}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 right-0 w-[20rem] h-full bg-white shadow-lg z-50 p-6 flex flex-col gap-4 lg:gap-6"
          >
            <div className="flex justify-between items-center mb-4">
              <img
                src={logo}
                alt="logo"
                className="h-12 cursor-pointer"
                onClick={() => handleNavigate("/")}
              />{" "}
              <button
                className="hover:bg-gray-100 p-1 rounded-full "
                onClick={() => setMenuOpen(false)}
              >
                <X className="size-6" />
              </button>
            </div>

            {menuItems.map((item, index) => {
              const isActive =
                item.path === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.path);

              return (
                <div key={index}>
                  {item.options ? (
                    <div>
                      <div
                        className="flex items-center justify-between text-primaryTextColor font-medium cursor-pointer"
                        onClick={() =>
                          setActiveDropdown(
                            activeDropdown === index ? null : index
                          )
                        }
                      >
                        {item.title}
                        <IoIosArrowDown />
                      </div>
                      {activeDropdown === index && (
                        <div className="ml-4 mt-2 space-y-2">
                          {item.options.map((opt, idx) => (
                            <div
                              key={idx}
                              className="text-sm text-secondaryTextColor hover:text-primaryColor cursor-pointer"
                              onClick={() => handleNavigate(opt.path)}
                            >
                              {opt.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`relative font-medium cursor-pointer transition ${isActive
                        ? "text-accentTextColor"
                        : "text-primaryTextColor hover:text-accentTextColor"
                        }`}
                      onClick={() => handleNavigate(item.path)}
                    >
                      {item.title}

                      {/* Active indicator */}
                      {isActive && (
                        <span className="absolute left-0 -bottom-1 w-6 h-[2px] bg-accentTextColor rounded"></span>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

            {user ? (
              <button
                onClick={handleLogout}
                className="mt-auto bg-primaryColor hover:opacity-90 active:scale-[0.98] text-white px-5 py-2.5 rounded-md w-full font-medium"
              >
                Logout
              </button>
            ) : (
              <button
                onClick={() => handleNavigate("/login")}
                className="mt-auto bg-primaryColor hover:opacity-90 text-white px-5 py-2.5 rounded-md w-full font-medium"
              >
                Login
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BottomNavbar;
